<template>
  <div
    class="tw-p-6"
    :class="{
      'two-drawer-layout__screen-height--after-secondary-subheader':
        !usingNewLayout,
    }"
  >
    <div
      class="
        tw-border
        tw-border-solid
        tw-border-sf-neutral-lighter-2
        tw-rounded-lg
        tw-h-full
        tw-w-condensed-content
        tw-max-w-condensed-content
        tw-mx-auto
        tw-flex
        tw-flex-row
        tw-w-full
      "
    >
      <div
        class="
          tw-h-full
          tw-shrink-0
          tw-grow
          tw-rounded-l-lg
          tw-p-4
          tw-max-w-condensed-sidepane
          tw-w-condensed-sidepane
          tw-overflow-y-auto
          tw-bg-sf-neutral-subtle-1
        "
      >
        <div v-if="loading">
          <SfSkeletonLoader
            v-for="i in 15"
            :key="i"
            height="2rem"
            :width="['10rem', '15rem', '20rem'][i % 3]"
            class="tw-px-2"
          />
        </div>
        <div v-else>
          <InboxLeftSidepane
            :selected-category="selectedCategory"
            :selected-item="selectedItem"
            @select="setSelected"
          >
            <template #before>
              <SfListItem
                item-id="pulse-inbox-overview"
                background="transparent"
                font-weight="normal"
                cornered
                :selected="isOverviewSelected"
                @item-click="setSelected(PULSE_OVERVIEW_CATEGORY, '')"
              >
                <template #icon>
                  <span :class="overviewIconClass">
                    <i class="fa fa-bar-chart" />
                  </span>
                </template>
                <template #default>
                  <span class="tw-font-medium">
                    {{ I18n.t('inbox.pulse.overview.title') }} -
                  </span>
                  <span
                    :class="{ 'tw-text-sf-gray-light': !isOverviewSelected }"
                  >
                    {{ I18n.t('inbox.pulse.overview.secondary_text') }}
                  </span>
                </template>
              </SfListItem>
              <template v-if="anyFilterPresent">
                <SfListItem
                  item-id="pulse-inbox-filter-results"
                  background="transparent"
                  font-weight="normal"
                  cornered
                  :selected="selectedCategory === FILTERED_CATEGORY"
                  @item-click="setSelected(FILTERED_CATEGORY, '')"
                >
                  <template #icon>
                    <span :class="filterCategoryIconClass">
                      <i class="fa fa-list-ul" />
                    </span>
                  </template>
                  <template #default>
                    <span class="tw-font-medium">
                      {{ I18n.t('inbox.company.filter_results.title') }}
                    </span>
                  </template>
                </SfListItem>
              </template>
              <div
                v-if="!empty"
                class="tw-flex tw-flex-col tw-justify-center tw-h-8"
              >
                <div class="tw-h-px tw-bg-sf-neutral-light-1" />
              </div>
            </template>
          </InboxLeftSidepane>
        </div>
      </div>
      <div
        data-testid="pulse-inbox-communications-container"
        class="
          tw-h-full
          tw-shrink
          tw-grow
          tw-overflow-y-auto
          tw-rounded-r-lg
          tw-bg-sf-snow-lighter
        "
      >
        <div v-if="loading">
          <RemarkSkeleton v-for="i in 4" :key="i" class="tw-mx-2 tw-my-1" />
        </div>
        <div
          v-else-if="isOverviewSelected"
          v-dompurify-html="renderedHTML"
          class="tw-p-2"
        />
        <InboxCommunicationContainer
          v-else
          :selected-category="selectedCategory"
          :selected-item="selectedItem"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import useI18n from '@app/shared/vue/composables/use-I18n'
import { computed, onBeforeUnmount, onMounted, watch, watchEffect } from 'vue'
import { useStore } from 'vuex'
import {
  ALL_CATEGORY,
  FILTERED_CATEGORY,
  PULSE_OVERVIEW_CATEGORY,
  UNREAD_CATEGORY,
} from '@app/application/communication/screens/inbox/store/utils'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import InboxLeftSidepane from '@app/application/communication/screens/inbox/components/inbox-left-sidepane.vue'
import RemarkSkeleton from '@app/shared/vue/components/molecules/remark/remark-skeleton.vue'
import InboxCommunicationContainer from '@app/application/communication/screens/inbox/components/inbox-communication-container.vue'
import { SfListItem } from '@silverfin/ui-components'
import { useInboxFiltersStore } from '@app/application/communication/screens/inbox/store/inbox-filters'
import { storeToRefs } from 'pinia'
import useGroupedFilteredCommunications from '@app/application/communication/screens/inbox/composables/use-grouped-filtered-communications'
import useUrlStorage from '@app/shared/vue/composables/use-url-storage'
import { isUsingNewLayout } from '@app/adapters/silverfin'

const store = useStore()
const I18n = useI18n()
const { groupedCommunication, filteredUnreadNotificationCount } =
  useGroupedFilteredCommunications()

const props = defineProps({
  renderedHTML: {
    type: String,
    required: true,
  },
  context: {
    type: Object,
    required: true,
  },
  initialCategory: {
    type: String,
    default: PULSE_OVERVIEW_CATEGORY,
  },
  initialItem: {
    type: String,
    default: '',
  },
})

const inboxFiltersStore = useInboxFiltersStore()

const selectedCategory = useUrlStorage(
  'category',
  props.initialCategory,
  (val) => val
)
const selectedItem = useUrlStorage('item', props.initialItem, (val) => val)

const filterCategoryIconClass = computed(() =>
  selectedCategory.value === FILTERED_CATEGORY
    ? 'tw-text-sf-gray-darker'
    : 'tw-text-sf-gray-light'
)

const usingNewLayout = isUsingNewLayout()

const { anyFilterPresent, receivedInitialValues } =
  storeToRefs(inboxFiltersStore)
watch(anyFilterPresent, () => {
  if (receivedInitialValues.value) {
    if (anyFilterPresent.value) {
      setSelected(FILTERED_CATEGORY, '')
    } else if (selectedCategory.value === FILTERED_CATEGORY) {
      setSelected(PULSE_OVERVIEW_CATEGORY, '')
    }
  }
})

const isOverviewSelected = computed(
  () => selectedCategory.value === PULSE_OVERVIEW_CATEGORY
)
const overviewIconClass = computed(() =>
  isOverviewSelected.value ? 'tw-text-sf-gray-darker' : 'tw-text-sf-gray-light'
)

const empty = computed(() => {
  return Object.keys(groupedCommunication.value).length === 0
})

const hasNotifications = computed(() => {
  return Object.keys(store.getters.notifications).length !== 0
})

const loading = computed(() => {
  return (
    (!hasNotifications.value && store.getters.loadingNotifications) ||
    store.getters.loadingWorkflows ||
    store.getters.loadingMentionUsernames ||
    (empty.value && store.getters.fetchingCommunicationsInProgress)
  )
})

function setSelected(category, item) {
  store.dispatch('resetNotificationReadCache')

  selectedCategory.value = category
  selectedItem.value = item
}

watchEffect(() => {
  if (loading.value) {
    return
  }

  if (
    selectedCategory.value === UNREAD_CATEGORY &&
    filteredUnreadNotificationCount.value === 0
  ) {
    setSelected(ALL_CATEGORY, '')
  }
})

async function refreshCommunicationData() {
  return Promise.all([
    store.dispatch('fetchCommunication'),
    store.dispatch('fetchNotifications'),
  ])
}

void Promise.all([
  store.dispatch('fetchWorkflows'),
  refreshCommunicationData(),
  store.dispatch('fetchMentionUsernames'),
])

onMounted(() => {
  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)
})

onBeforeUnmount(() => {
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
})
</script>
